import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useGlobalManualLocationOrder } from "@/contexts/Staffing/GlobalManualLocationOrderContext";
import { useJobs } from "@/hooks/useJobs";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import {
	postDepartmentLocations,
	putDepartmentLocation,
	useDeleteDepartmentLocations,
	useUpdateSingleDepartmentLocation,
} from "@/pages/Settings/DepartmentConfiguration/forms/hooks";
import { backendService } from "@/services/backend.service";
import { errorText } from "@/utils/errors";
import { type DepartmentLocation, EditDepartmentLocationConfigMode, NurseFullRole, type NurseRole } from "@/utils/models";
import { addDayAmountRow, disabledDaysByRow, hasOccupiedDaysByRow, removeDayAmountRow, updateDayAmountRow } from "./dayAmountRowOps";
import { buildFormRow, type DayAmountRow, getInitialConfigData, type LocationConfigSlimData, shiftTimesOf } from "./locationConfigData";
import { locationConfigProblem, problemMessage, ratioLadderProblemOf, type ShiftConfigContext } from "./locationConfigProblems";
import {
	censusChoiceAfterAreaChange,
	censusChoiceAfterNamePick,
	type EmrAutofill,
	type EmrMapping,
	initialDepartmentWideCensus,
	pickLocationName,
	uniqueLocationNames,
} from "./locationEmrMapping";
import { toLocationRoles } from "./ratioLadderCoverage";
import { syncLadderOwner } from "./ratioLadderOwner";
import { diffShiftMatchKey, pickShiftMatchKey, type ShiftMatchChange, type ShiftMatchKey, willCascadeOntoShifts } from "./shiftMatchKey";
import { roomsNoticeFor, useCensusAreaOptions } from "./useCensusAreaOptions";
import { useFormRowContext } from "./useFormRowContext";

export type LocationUpdateInfo = {
	didEdit?: boolean;
	refreshDepartmentData?: boolean;
};

export type EditDepartmentLocationConfigProps = {
	mode: EditDepartmentLocationConfigMode;
	existingConfig?: DepartmentLocation;
	onDidChangeNotifier?: () => void;
	onCancel: () => void;
	onSave: (updatedInfo?: LocationUpdateInfo) => void;
	/** Which days are occupied by existing shifts 0-6 (sun-sat) */
	occupiedDays: number[];
	uniqueSections: string[];
	onSomeDropdownOpenChange: (isOpen: boolean) => void;
	isSynergyMS: boolean;
	/** Create-mode only: pre-select this section (e.g. the section band the "+" was clicked on). */
	initialSection?: string;
	/**
	 * Hide the editable location-NAME text field (default false). For surfaces where the name is owned
	 * by a dedicated rename flow (the Locations page): the config editor still saves the existing name
	 * untouched, it just can't be edited inline here. Additive/gated — existing callers are unaffected.
	 */
	hideNameField?: boolean;
};

export const useEditDepartmentLocationConfigState = ({
	existingConfig,
	mode,
	onSave,
	onDidChangeNotifier,
	occupiedDays,
	onCancel,
	uniqueSections,
	onSomeDropdownOpenChange,
	isSynergyMS,
	initialSection,
}: EditDepartmentLocationConfigProps) => {
	const { timezone, chosenDepartment, setChosenDepartment, isMedicalDepartment, showErrorSnackbar } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const enabledJobs = useJobs();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { t } = useTranslation();
	const formRowContext = useFormRowContext();
	const { canEditRatioLadder } = formRowContext;

	// --- State ---
	const [configSlimData, setConfigSlimData] = useState<LocationConfigSlimData>(() =>
		getInitialConfigData(canEditRatioLadder, chosenDepartment.roles, isMedicalDepartment, mode, existingConfig, initialSection),
	);
	const [departmentWideCensus, setDepartmentWideCensus] = useState<boolean>(() => initialDepartmentWideCensus(existingConfig, canEditRatioLadder));
	/** The last name pick's EMR fill-in, so picking a name without a mapping can take it back. */
	const emrAutofillRef = useRef<EmrAutofill | null>(null);
	/**
	 * What the popup suggested when it opened, frozen at mount — our only handle on the match key
	 * the orphaned recurring shifts still carry. Snapshotted rather than read back off
	 * `existingConfig` because `getInitialConfigData` rewrites the times for medical departments,
	 * which would otherwise read as divergence on every one of them.
	 */
	const suggestedMatchKeyRef = useRef<ShiftMatchKey>(pickShiftMatchKey(configSlimData));
	const [updateFromDate, setUpdateFromDate] = useState<DateTime>(() => DateTime.now().setZone(timezone).startOf("day"));
	const [datePickerAnchorEl, setDatePickerAnchorEl] = useState<HTMLElement | null>(null);
	const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
	const [editTimeOpen, setEditTimeOpen] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	const [uniqueLocationsOptions, setUniqueLocationsOptions] = useState<string[]>(() =>
		initialSection && !uniqueSections.includes(initialSection) ? [...uniqueSections, initialSection] : uniqueSections,
	);
	const [selectedSectionOption, setSelectedSectionOption] = useState<string>(configSlimData.section);

	// --- Memos ---
	const locationRoles = useMemo<NurseRole[]>(
		() => toLocationRoles(configSlimData.roles, chosenDepartment.roles),
		[configSlimData.roles, chosenDepartment.roles],
	);

	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const locationNameOptions = useMemo<string[]>(
		() => uniqueLocationNames(chosenDepartment.locations, configSlimData.name, customStringOrderMap),
		[chosenDepartment.locations, configSlimData.name, customStringOrderMap],
	);

	const locationJobId = useMemo<string>(() => configSlimData.jobId, [configSlimData.jobId]);

	/**
	 * The Weekly and Synergy tabs open this popup in Create mode on an "out of config" row,
	 * pre-filled from a synthetic DepartmentLocation that carries no id. Adopting that suggestion
	 * as-is is safe — the created config matches the orphaned recurring shifts by value — but
	 * diverging from it is not, because POST /departments/locations never touches recurring_shift
	 * or nurse_in_shift.
	 */
	const isOutOfConfigAdoption = mode === EditDepartmentLocationConfigMode.Create && !!existingConfig && !existingConfig.id;

	const shiftMatchChanges = useMemo<ShiftMatchChange[]>(
		() => (isOutOfConfigAdoption ? diffShiftMatchKey(suggestedMatchKeyRef.current, pickShiftMatchKey(configSlimData), jobManagementEnabled) : []),
		[isOutOfConfigAdoption, configSlimData, jobManagementEnabled],
	);
	/** Saving would orphan existing shifts unless we follow the create with a cascading edit. */
	const willUpdateExistingShifts = shiftMatchChanges.length > 0;

	/**
	 * Edit mode only: whether the save will actually reach existing shifts. The server skips the
	 * cascade when no shift-relevant field moved, which makes the "apply from" date inert — so the
	 * picker is disabled rather than silently ignored. An out-of-config create always cascades (that
	 * is the point of its second leg), so it is not gated.
	 */
	const applyFromDateAffectsShifts = useMemo(
		() => (mode === EditDepartmentLocationConfigMode.Edit && existingConfig?.id ? willCascadeOntoShifts(existingConfig, configSlimData) : true),
		[mode, existingConfig, configSlimData],
	);

	// --- Mutations ---
	const {
		mutateAsync: deleteDepartmentLocations,
		isPending: deleteLoading,
		isSuccess: deleteSuccess,
	} = useDeleteDepartmentLocations({
		// DEFAULT: "" in Create mode, where there's no saved row to delete. The hook needs an id at mount,
		// but delete is only offered in Edit mode and handleDeleteLocation only runs with an existingConfig,
		// so the empty id is never sent.
		id: existingConfig?.id ?? "",
	});

	const { mutateAsync: updateSingleLocation, isPending: updateSingleLocationLoading } = useUpdateSingleDepartmentLocation();
	const { availableUnits, availableFloors, availableRooms, emrOptionsAvailable, offer: offerEmrMapping, scopeProblem } = useCensusAreaOptions();
	/** The area the popup counts and saves: only the unit, floor and rooms the EMR lists offer. */
	const offeredMapping = useMemo<EmrMapping>(
		() => offerEmrMapping({ unit: configSlimData.unit, floor: configSlimData.floor, rooms: configSlimData.rooms }),
		[offerEmrMapping, configSlimData.unit, configSlimData.floor, configSlimData.rooms],
	);

	// --- Effects ---

	// Auto-populate jobId when editing an existing location that has roles but no job yet
	const didAutoPopulateJobId = useRef(false);
	useEffect(() => {
		if (didAutoPopulateJobId.current) return;
		if (!jobManagementEnabled || configSlimData.jobId || !enabledJobs.length) return;
		if (mode === EditDepartmentLocationConfigMode.Edit && existingConfig?.roles?.length) {
			didAutoPopulateJobId.current = true;
			const firstJobId = enabledJobs[0].id;
			setConfigSlimData((prev) => ({ ...prev, jobId: firstJobId }));
		}
	}, [jobManagementEnabled, configSlimData.jobId, enabledJobs, mode, existingConfig?.roles?.length]);

	useEffect(() => {
		if (deleteSuccess && existingConfig?.id) {
			setChosenDepartment((prevState) => ({
				...prevState,
				locations: prevState.locations.filter((loc) => loc.id !== existingConfig.id),
			}));
			onSave();
		}
	}, [deleteSuccess, setChosenDepartment, existingConfig, onSave]);

	// --- Handlers ---
	const handleDeleteLocation = useCallback(async () => {
		if (existingConfig) {
			await deleteDepartmentLocations();
		}
	}, [deleteDepartmentLocations, existingConfig]);

	/**
	 * Save handler for per-day amounts (edit mode).
	 * Groups days by (amount, amountRequired) and updates/creates/deletes location entries.
	 */
	const handleSaveWithDayAmounts = useCallback(async () => {
		setSaving(true);
		try {
			track("schedule_template_day_amounts_save_start", {
				department_id: chosenDepartment.department_id,
				location_name: configSlimData.name,
				days: configSlimData.days.join(","),
			});

			if (!existingConfig) return;

			const updatedLocation = buildFormRow(configSlimData, offeredMapping, existingConfig.id, existingConfig, formRowContext);

			const applyFromDate = updateFromDate.toFormat("yyyy-MM-dd");
			const res = await updateSingleLocation({ updatedLocation, applyFromDate });
			// DEFAULT: no shifts moved, moved verbatim. The PUT's response model always carries
			// amountOfUpdatedShifts (0 when nothing moved), so this only decides what a response without it
			// would mean: no shifts moved, so no department refresh.
			const hasUpdatedShifts = (res?.amountOfUpdatedShifts ?? 0) > 0;

			setChosenDepartment((prevState) => ({
				...prevState,
				locations: prevState.locations.map((loc) => (loc.id === existingConfig.id ? updatedLocation : loc)),
			}));

			track("schedule_template_day_amounts_save_success", {
				department_id: chosenDepartment.department_id,
				location_name: configSlimData.name,
			});

			onSave({ didEdit: true, refreshDepartmentData: hasUpdatedShifts });
		} catch (error) {
			trackError("schedule_template_day_amounts_save_error", {
				department_id: chosenDepartment.department_id,
				error_message: errorText(error),
			});
			showErrorSnackbar("schedule.template.save.error", {}, error);
		} finally {
			setSaving(false);
		}
	}, [
		formRowContext,
		chosenDepartment.department_id,
		configSlimData,
		offeredMapping,
		existingConfig,
		setChosenDepartment,
		onSave,
		updateSingleLocation,
		updateFromDate,
		showErrorSnackbar,
		track,
		trackError,
	]);

	/**
	 * Second leg of the out-of-config adoption: the config now exists under the *suggested* match
	 * key, so PUT it to the user's real values. That endpoint — unlike the bulk POST — cascades the
	 * change onto the matching recurring shifts and their generated shifts.
	 *
	 * Owns its own error reporting and never rethrows, so a handled failure here does not also
	 * raise the caller's generic save error.
	 */
	const performOutOfConfigAdoption = useCallback(
		async (newLocationId: string, createdLocations: DepartmentLocation[]) => {
			const department_id = chosenDepartment.department_id;
			try {
				// Build on the row the server just returned, not on the defaults, so anything it
				// assigned during the create survives the edit.
				const createdLocation = createdLocations.find((loc) => loc.id === newLocationId);
				if (!createdLocation) {
					throw new Error("The created location is missing from the create response");
				}
				const updatedLocation = buildFormRow(configSlimData, offeredMapping, newLocationId, createdLocation, formRowContext);
				const applyFromDate = updateFromDate.toFormat("yyyy-MM-dd");
				// Marks the PUT specifically, not the handler as a whole — `..._create_start` already
				// fired before the POST, so without this a session that dies mid-flight cannot be
				// placed on either side of the second call.
				track("schedule_template_out_of_config_adopt_start", {
					department_id,
					location_id: newLocationId,
					location_name: configSlimData.name,
					apply_from_date: applyFromDate,
					shift_match_changes: shiftMatchChanges.map((change) => change.field).join(","),
				});
				// Deliberately the PUT core, not useUpdateSingleDepartmentLocation: that hook's onError
				// always raises its own `location.config.update.error` snackbar, which would stack on
				// top of the message this orchestration owns. Same reason the create above posts
				// through the POST core directly.
				const { amountOfUpdatedShifts } = await putDepartmentLocation(department_id, { updatedLocation, applyFromDate });
				setChosenDepartment((prevState) => ({
					...prevState,
					locations: createdLocations.map((loc) => (loc.id === newLocationId ? updatedLocation : loc)),
				}));
				track("schedule_template_out_of_config_adopt_success", {
					department_id,
					location_name: configSlimData.name,
					amount_of_updated_shifts: amountOfUpdatedShifts,
				});
				onSave({ didEdit: true, refreshDepartmentData: amountOfUpdatedShifts > 0 });
			} catch (adoptError) {
				// No transaction spans the two calls, so undo the config we just created and leave
				// the user where they started.
				try {
					await backendService.deleteDepartmentLocations(department_id, newLocationId);
					trackError("schedule_template_out_of_config_adopt_error", { department_id, error_message: errorText(adoptError) });
					showErrorSnackbar("schedule.template.updateShifts.failed", {}, adoptError);
				} catch (rollbackError) {
					// Both calls failed: a config now exists under the suggested name that did not
					// before. Commit it and say so exactly, rather than "nothing was changed".
					// Carry the PUT failure too — it is the reason the rollback ran, and this branch
					// is the only place it would otherwise go unrecorded.
					trackError("schedule_template_out_of_config_adopt_rollback_error", {
						department_id,
						error_message: errorText(rollbackError),
						adopt_error_message: errorText(adoptError),
					});
					setChosenDepartment((prevState) => ({ ...prevState, locations: createdLocations }));
					showErrorSnackbar("schedule.template.updateShifts.rollback.failed", { locationName: suggestedMatchKeyRef.current.name }, rollbackError);
					onSave({ didEdit: true, refreshDepartmentData: true });
				}
			}
		},
		[
			chosenDepartment.department_id,
			configSlimData,
			offeredMapping,
			formRowContext,
			updateFromDate,
			setChosenDepartment,
			onSave,
			showErrorSnackbar,
			shiftMatchChanges,
			track,
			trackError,
		],
	);

	/**
	 * Create handler for per-day amounts.
	 * Groups days by (amount, amountRequired) and creates one location per group.
	 */
	const handleCreateWithDayAmounts = useCallback(async () => {
		setSaving(true);
		try {
			track("schedule_template_day_amounts_create_start", {
				department_id: chosenDepartment.department_id,
				location_name: configSlimData.name,
				days: configSlimData.days.join(","),
			});

			const newLocationId = uuidv4();
			// When the user diverged from the suggestion, create under the *suggested* match key so
			// the new config lines up with the orphaned recurring shifts; the adoption step below
			// then moves both to the values they actually asked for. The match key (name, hours, roles,
			// job) is none of what the ladder or the census area sets, so it lands the same either side of them.
			const dataForInitialCreate: LocationConfigSlimData = willUpdateExistingShifts ? { ...configSlimData, ...suggestedMatchKeyRef.current } : configSlimData;
			const newLocation = buildFormRow(dataForInitialCreate, offeredMapping, newLocationId, {}, formRowContext);

			const savedLocations = await postDepartmentLocations(chosenDepartment.department_id, {
				new: [newLocation],
				existing: chosenDepartment.locations,
			});
			if (savedLocations.length === 0) {
				throw new Error("Creating the location returned no locations");
			}

			if (willUpdateExistingShifts) {
				await performOutOfConfigAdoption(newLocationId, savedLocations);
				return;
			}

			setChosenDepartment((prevState) => ({
				...prevState,
				locations: savedLocations,
			}));

			track("schedule_template_day_amounts_create_success", {
				department_id: chosenDepartment.department_id,
				location_name: configSlimData.name,
			});

			onSave();
		} catch (error) {
			trackError("schedule_template_day_amounts_create_error", {
				department_id: chosenDepartment.department_id,
				error_message: errorText(error),
			});
			showErrorSnackbar("schedule.template.save.error", {}, error);
		} finally {
			setSaving(false);
		}
	}, [
		formRowContext,
		chosenDepartment.department_id,
		chosenDepartment.locations,
		configSlimData,
		offeredMapping,
		setChosenDepartment,
		onSave,
		showErrorSnackbar,
		willUpdateExistingShifts,
		performOutOfConfigAdoption,
		track,
		trackError,
	]);

	const didChange = useRef<boolean>(false);
	const handleSetConfigSlimData = useCallback(
		(newData: Partial<LocationConfigSlimData>) => {
			setConfigSlimData((prevState) => {
				const updated = { ...prevState, ...newData };
				return updated;
			});
			if (!didChange.current) {
				didChange.current = true;
				if (onDidChangeNotifier) {
					onDidChangeNotifier();
				}
			}
		},
		[onDidChangeNotifier],
	);

	// Sync ratio ladder entry roles/jobId when location roles/job change
	const prevLocationRolesRef = useRef<NurseRole[]>(locationRoles);
	const prevLocationJobIdRef = useRef<string>(locationJobId);
	useEffect(() => {
		if (!canEditRatioLadder || configSlimData.ratioLadder.length === 0) return;
		// Only the dimension `syncLadderOwner` compares is advanced; the other keeps its last compared value.
		const prev = { roles: prevLocationRolesRef.current, jobId: prevLocationJobIdRef.current };
		if (jobManagementEnabled) prevLocationJobIdRef.current = locationJobId;
		else prevLocationRolesRef.current = locationRoles;
		const synced = syncLadderOwner(configSlimData.ratioLadder, prev, { roles: locationRoles, jobId: locationJobId }, jobManagementEnabled);
		if (synced !== null) handleSetConfigSlimData({ ratioLadder: synced });
	}, [locationRoles, locationJobId, jobManagementEnabled, canEditRatioLadder, configSlimData.ratioLadder, handleSetConfigSlimData]);

	const handleAddRow = useCallback(() => {
		handleSetConfigSlimData(addDayAmountRow(configSlimData.dayAmountRows, uuidv4()));
	}, [configSlimData.dayAmountRows, handleSetConfigSlimData]);

	const handleRemoveRow = useCallback(
		(rowId: string) => {
			handleSetConfigSlimData(removeDayAmountRow(configSlimData.dayAmountRows, rowId));
		},
		[configSlimData.dayAmountRows, handleSetConfigSlimData],
	);

	const handleUpdateRow = useCallback(
		(rowId: string, updates: Partial<DayAmountRow>) => {
			handleSetConfigSlimData(updateDayAmountRow(configSlimData.dayAmountRows, rowId, updates));
		},
		[configSlimData.dayAmountRows, handleSetConfigSlimData],
	);

	const rowHasOccupiedDays = useMemo(() => hasOccupiedDaysByRow(configSlimData.dayAmountRows, occupiedDays), [occupiedDays, configSlimData.dayAmountRows]);

	const disabledDaysPerRow = useMemo(() => disabledDaysByRow(configSlimData.dayAmountRows), [configSlimData.dayAmountRows]);

	const handleUpdateFromDate = useCallback(
		(newDate: DateTime | null) => {
			if (newDate) {
				setUpdateFromDate(newDate);
				if (!didChange.current) {
					didChange.current = true;
					if (onDidChangeNotifier) {
						onDidChangeNotifier();
					}
				}
			}
		},
		[onDidChangeNotifier],
	);

	const editTimePopoverParentRef = useRef<HTMLDivElement>(null);
	const handleSelectedRoleFiltersChanged = useCallback(
		(newSelectedRoles: (NurseRole | "all")[]) => {
			handleSetConfigSlimData({
				roles: newSelectedRoles.map((r) => (r === "all" ? "all" : NurseFullRole[r])),
			});
		},
		[handleSetConfigSlimData],
	);

	const handleSelectedJobChanged = useCallback(
		(newJobId: string) => {
			handleSetConfigSlimData({ jobId: newJobId });
		},
		[handleSetConfigSlimData],
	);

	const times = useMemo(
		() => shiftTimesOf({ shiftStartTime: configSlimData.shiftStartTime, shiftEndTime: configSlimData.shiftEndTime }, timezone),
		[configSlimData.shiftStartTime, configSlimData.shiftEndTime, timezone],
	);

	const problemContext = useMemo<ShiftConfigContext>(
		() => ({
			timezone,
			isSynergyMS,
			jobManagementEnabled,
			canEditRatioLadder,
			departmentRoles: chosenDepartment.roles,
			enabledJobCount: enabledJobs.length,
		}),
		[timezone, isSynergyMS, jobManagementEnabled, canEditRatioLadder, chosenDepartment.roles, enabledJobs.length],
	);
	/** Why the ladder can't be saved yet (ratio departments only), or null once it covers 0–∞. */
	const ratioLadderProblem = useMemo(
		() => ratioLadderProblemOf({ roles: configSlimData.roles, ratioLadder: configSlimData.ratioLadder }, problemContext),
		[configSlimData.roles, configSlimData.ratioLadder, problemContext],
	);
	const ratioLadderGapText = useMemo<string | null>(
		() => (ratioLadderProblem?.problem.kind === "gap" ? problemMessage(ratioLadderProblem, t) : null),
		[ratioLadderProblem, t],
	);

	const censusScope = scopeProblem(offeredMapping, departmentWideCensus);
	const roomsNotice = roomsNoticeFor(censusScope);

	const saveDisabledReason = useMemo<string | null>(() => {
		const problem = locationConfigProblem(configSlimData, { ...problemContext, censusScope });
		return problem === null ? null : problemMessage(problem, t);
	}, [configSlimData, problemContext, censusScope, t]);

	const isSavedEnabled = useMemo(() => {
		return !saveDisabledReason;
	}, [saveDisabledReason]);

	const addSectionOption = useCallback(
		(newOption: string) => {
			const trimmed = newOption.trim();
			if (!uniqueLocationsOptions.find((val) => val.toLowerCase() === trimmed.toLowerCase())) {
				const updatedOptions = [...uniqueLocationsOptions, trimmed];
				setUniqueLocationsOptions(updatedOptions);
			}
			setSelectedSectionOption(trimmed);
			handleSetConfigSlimData({ section: trimmed });
		},
		[uniqueLocationsOptions, handleSetConfigSlimData],
	);
	const ondDropdownOptionSelect = useCallback(
		(selectedValue?: string | undefined) => {
			if (selectedValue != null /*dont want null or undefined but its ok to have no section aka empty string*/) {
				const trimmed = selectedValue.trim();
				setSelectedSectionOption(trimmed);
				handleSetConfigSlimData({ section: trimmed });
			}
		},
		[handleSetConfigSlimData],
	);
	/**
	 * Picking or typing a name. An existing name reuses how that location is already mapped to the EMR;
	 * every field stays editable. The dropdown only calls this with a non-blank, trimmed value.
	 */
	const handleSelectLocationName = useCallback(
		(picked: string | undefined) => {
			if (picked == null) return; // the name is required, so the dropdown offers no "clear"
			const pick = pickLocationName(chosenDepartment.locations, picked, configSlimData.name, existingConfig?.id);
			if (pick === null) return;
			const current = {
				departmentWideCensus,
				mapping: { unit: configSlimData.unit, floor: configSlimData.floor, rooms: configSlimData.rooms },
			};
			const { choice, autofill } = censusChoiceAfterNamePick(current, pick.mapping, emrAutofillRef.current);
			emrAutofillRef.current = autofill;
			setDepartmentWideCensus(choice.departmentWideCensus);
			handleSetConfigSlimData({ name: pick.name, ...choice.mapping });
		},
		[
			chosenDepartment.locations,
			configSlimData.name,
			configSlimData.unit,
			configSlimData.floor,
			configSlimData.rooms,
			departmentWideCensus,
			existingConfig?.id,
			handleSetConfigSlimData,
		],
	);
	/**
	 * Editing the unit, floor or rooms. Choosing an area unticks Department wide — reachable only where the
	 * fields stay open while it is ticked (PPD/HPPD; see `LocationEmrMappingSection`).
	 */
	const handleEmrMappingChange = useCallback(
		(changes: Partial<EmrMapping>) => {
			const current = { departmentWideCensus, mapping: { unit: configSlimData.unit, floor: configSlimData.floor, rooms: configSlimData.rooms } };
			setDepartmentWideCensus(censusChoiceAfterAreaChange(current, changes).departmentWideCensus);
			handleSetConfigSlimData(changes);
		},
		[departmentWideCensus, configSlimData.unit, configSlimData.floor, configSlimData.rooms, handleSetConfigSlimData],
	);
	/** Department wide and an EMR area are exclusive: ticking it clears unit, floor and rooms. */
	const handleDepartmentWideCensusChange = useCallback(
		(checked: boolean) => {
			setDepartmentWideCensus(checked);
			handleSetConfigSlimData(checked ? { unit: undefined, floor: undefined, rooms: [] } : {});
		},
		[handleSetConfigSlimData],
	);
	const handleSomeDropdownOpen = useCallback(() => onSomeDropdownOpenChange(true), [onSomeDropdownOpenChange]);
	const handleSomeDropdownClose = useCallback(() => onSomeDropdownOpenChange(false), [onSomeDropdownOpenChange]);

	return {
		configSlimData,
		updateFromDate,
		datePickerAnchorEl,
		setDatePickerAnchorEl,
		editTimePopoverParentRef,
		editTimeOpen,
		setEditTimeOpen,
		times,
		handleSelectedRoleFiltersChanged,
		isSavedEnabled,
		mode,
		existingConfig,
		onCancel,
		handleDeleteLocation,
		deleteLoading,
		setConfirmDelete,
		confirmDelete,
		updateDepartmentLocationsLoading: deleteLoading || updateSingleLocationLoading || saving,
		handleSetConfigSlimData,
		handleUpdateFromDate,
		occupiedDays,
		allowDelete: occupiedDays.length === 0,
		addSectionOption,
		selectedSectionOption,
		uniqueLocationsOptions,
		ondDropdownOptionSelect,
		handleSomeDropdownOpen,
		handleSomeDropdownClose,
		isSynergyMS,
		saveDisabledReason,
		availableUnits,
		availableFloors,
		availableRooms,
		emrOptionsAvailable,
		roomsNotice,
		offeredMapping,
		departmentWideCensus,
		handleDepartmentWideCensusChange,
		handleEmrMappingChange,
		locationNameOptions,
		handleSelectLocationName,
		canEditRatioLadder,
		locationRoles,
		ratioLadderGapText,
		handleAddRow,
		handleRemoveRow,
		handleUpdateRow,
		disabledDaysPerRow,
		handleSaveWithDayAmounts,
		handleCreateWithDayAmounts,
		shiftMatchChanges,
		willUpdateExistingShifts,
		applyFromDateAffectsShifts,
		rowHasOccupiedDays,
		jobManagementEnabled,
		handleSelectedJobChanged,
		locationJobId,
	};
};
